import React, { useState, useRef, useEffect } from 'react';
import { Box, Container, Typography, IconButton } from '@mui/material';
import UploadIcon from '@mui/icons-material/Upload'; 
import DeleteIcon from '@mui/icons-material/Delete';
import Heading from '../../Components/heading.tsx';
import { getDocuments, addDocuments, deleteDocument } from './action.ts';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import imageCompression from 'browser-image-compression';
import ConfirmationPopup from '../../../WebApp/Components/ConfirmationModal/confirmation-modal.tsx';
import FooterLogo from '../../Components/footer.tsx';

// --- PDF RENDERING IMPORTS ---
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Initialize PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const styles = {
  statusContainer: {
    backgroundColor: '#f0f0f0', 
    paddingBottom: '16px',
    borderRadius: '8px',
    width: '100%',
    marginTop: '30px',
  },
  documentBox: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#fff',
    border: '1px solid #ccc',
    borderRadius: '8px',
    padding: '10px',
    marginBottom: '10px',
  },
  documentContainer: {
    backgroundColor: '#f0f0f0',
    paddingRight: '0px',
    paddingLeft: '0px',
  },
  inputFile: {
    display: 'none',
  },
  actionContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  viewLabel: {
    marginRight: '8px',
    color: '#02e8ee',
    cursor: 'pointer',
  },
  imageViewerBox: {
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
    backgroundColor: '#f0f0f0',
  },
  imageViewer: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain' as const,
  },
  pdfViewerBox: {
    width: '100%',
    height: '100%',
    overflowY: 'auto' as const, 
    backgroundColor: '#e0e0e0',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  backButton: {
    marginTop: '25px',
    marginLeft:'5px',
    color: 'black',
  }
};

const DocumentsScreen: React.FC = () => {
  const documentMapping: { [key: string]: string } = {
    'aadhar': 'Aadhar Card',
    'drivers license': 'Driving License',
    'pancard': 'PAN Card', 
    'registration certificate': 'Registration Certificate',
    'pollution certificate': 'Pollution Certificate',
    'fitness certificate': 'Fitness Certificate',
    'insurance': 'Insurance',
  };
  const getDocumentMapping: { [key: string]: string } = {
    'aadhar': 'Aadhar Card',
    'driving license': 'Driving License',
    'pancard': 'PAN Card',
    'registration certificate': 'Registration Certificate',
    'pollution certificate': 'Pollution Certificate',
    'fitness certificate': 'Fitness Certificate',
    'insurance': 'Insurance',
  };
  
  const [uploadedUserFiles, setUploadedUserFiles] = useState<{ [key: string]: { name: string; location: string; id: number | null} | null }>({});
  const [uploadedVehicleFiles, setUploadedVehicleFiles] = useState<{ [key: string]: { name: string; location: string; id: number | null } | null }>({});
  const [isViewingDocument, setIsViewingDocument] = useState(false);
  const [documentUrl, setDocumentUrl] = useState('');
  const [isUploading, setIsUploading] = useState<{ [key: string]: boolean }>({});
  
  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<string | null>(null);
  
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [currentDocument, setCurrentDocument] = useState<string>('');
  const navigate = useNavigate();

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    let file = event.target.files?.[0];
    if (file && currentDocument) {
      setIsUploading(prev => ({ ...prev, [currentDocument]: true }));

      if (file.size / 1024 / 1024 > 2 && file.type.startsWith('image/')) {
        try {
          const options = {
            maxSizeMB: 2,
            maxWidthOrHeight: 1920,
            useWebWorker: true,
          };
          const compressedBlob = await imageCompression(file, options);
          file = new File([compressedBlob], file.name, { type: file.type });
        } catch (err) {
          console.error('Image compression error:', err);
          toast.error('Failed to compress image. Uploading original file.');
        }
      }
      
      if (currentDocument === 'aadhar' || currentDocument === 'drivers license' || currentDocument === 'pancard') {
        setUploadedUserFiles(prev => ({ 
          ...prev, 
          [currentDocument]: { name: file.name, location: '', id: null } 
        }));
      } else {
        setUploadedVehicleFiles(prev => ({ 
          ...prev, 
          [currentDocument]: { name: file.name, location: '', id: null } 
        }));
      }

      handleUpload(currentDocument, file);
    }
  };

  const handleUpload = (document: string, file: File) => {
    const isUserDocument = document === 'driving license' || document === 'aadhar' ||document === 'pancard'; 
    
    const successCallback = (response: any) => {
      setIsUploading(prev => ({ ...prev, [document]: false }));
      
      if (isUserDocument) {
        setUploadedUserFiles(prevFiles => ({
          ...prevFiles,
          [document]: { name: file.name, location: response.message.location, id: response.message.id }
        }));
      } else {
        setUploadedVehicleFiles(prevFiles => ({
          ...prevFiles,
          [document]: { name: file.name, location: response.message.location, id: response.message.id }
        }));
      }
      toast.success(`${isUserDocument ? getDocumentMapping[document] : documentMapping[document]} uploaded successfully`);
    };

    const errorCallback = (errMessage: any) => {
      setIsUploading(prev => ({ ...prev, [document]: false }));
      if (isUserDocument) {
        setUploadedUserFiles(prev => ({ ...prev, [document]: null }));
      } else {
        setUploadedVehicleFiles(prev => ({ ...prev, [document]: null }));
      }
      toast.error(errMessage?.message || "An unexpected error occurred.");
    };

    if (isUserDocument) {
      addDocuments(
        document === 'driving license' ? file : null, document === 'aadhar' ? file : null, null, null, null, null,
        '', userId, document === 'pancard' ? file : null, successCallback, errorCallback
      );
    } else {
      addDocuments(
        null, null, document === 'registration certificate' ? file : null, document === 'insurance' ? file : null,
        document === 'fitness certificate' ? file : null, document === 'pollution certificate' ? file : null,
        Number(vehicleId), userId, null, successCallback, errorCallback
      );
    }
  };

  const askCameraPermission = async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ video: true });
      return true;
    } catch (err) {
      toast.error("Camera permission denied");
      return false;
    }
  };

  const handleUploadClick = async (document: string) => {
    setCurrentDocument(document);
    const granted = await askCameraPermission();
    if (!granted) return;

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleDeleteClick = (documentKey: string) => {
    setDocumentToDelete(documentKey);
    setIsDeletePopupOpen(true);
  };

  const handleConfirmDelete = () => {
    if (!documentToDelete) return;

    const documentKey = documentToDelete;
    const usersDocumentDetails = uploadedUserFiles[documentKey];
    const vehiclesDocumentDetails = uploadedVehicleFiles[documentKey];
    
    if (!usersDocumentDetails?.id && !vehiclesDocumentDetails?.id) {
      toast.error('Document not found or invalid.');
      setIsDeletePopupOpen(false);
      setDocumentToDelete(null);
      return;
    }

    const userDocumentId = usersDocumentDetails?.id;
    const vehicleDocumentId = vehiclesDocumentDetails?.id;
    
    deleteDocument(
      userDocumentId, vehicleDocumentId, vehicleId, userId,
      (response) => {
        toast.success(response?.message);
        setUploadedUserFiles((prev) => ({ ...prev, [documentKey]: null }));
        setUploadedVehicleFiles((prev) => ({ ...prev, [documentKey]: null }));
      },
      (errorMessage) => {
        toast.error(errorMessage.data.message);
      }
    );

    setIsDeletePopupOpen(false);
    setDocumentToDelete(null);
  };

  const handleCancelDelete = () => {
    setIsDeletePopupOpen(false);
    setDocumentToDelete(null);
  };

  const handleBack = () => {
    navigate(`/wv/dashboard?user_name=${userName}&vehicle_id=${vehicleId}&user_id=${userId}&token=${token}`);
  }

  const handleDocumentBack = () => {
    setIsViewingDocument(false);
    setPdfError(null);
  };
  
  const params = new URLSearchParams(window.location.search);
  const vehicleId = params.get('vehicle_id');
  const userId = params.get('user_id');
  const token= params.get('token') || '';
  const userName = params.get('user_name') || '';
  const vehicleNumber = params.get('vehicle_number') || '';

  useEffect(() => {
    getDocuments(
      vehicleId,
      (response: any) => {
        const userDocumentDetails = response.message?.user_documents || [];
        const uploadedUserDocuments: { [key: string]: { id: number; name: string; location: string } } = {};
        const vehicleDocumentDetails = response.message?.vehicle_documents || [];
        const uploadedVehicleDocuments: { [key: string]: { id: number; name: string; location: string } } = {};

        userDocumentDetails.forEach(doc => {
          if (doc.status === 'uploaded') {
            uploadedUserDocuments[doc.document_name] = { id: doc.id, name: getDocumentMapping[doc.document_name] || doc.document_name, location: doc.location };
          }
        });

        vehicleDocumentDetails.forEach(doc => {
          if (doc.status === 'uploaded') {
            uploadedVehicleDocuments[doc.document_name] = { id: doc.id, name: documentMapping[doc.document_name] || doc.document_name, location: doc.location };
          }
        });

        setUploadedUserFiles(uploadedUserDocuments);
        setUploadedVehicleFiles(uploadedVehicleDocuments);
      },
      (errMessage: any) => toast.error(errMessage)
    );
  }, [vehicleId]);
  
  const handleViewClick = (document: string, location: string) => {
    const url = `${global.documents_image_url}${location}`;
    setDocumentUrl(url);
    setIsViewingDocument(true);
    setPdfError(null);
  };

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPdfError(null);
  };

  const isPdf = documentUrl.toLowerCase().includes('.pdf');

  return (
    <Container style={styles.documentContainer}>
      {isViewingDocument ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', width: '100vw', overflow: 'hidden' }}>
          <Box sx={{ flexShrink: 0 }}>
            <IconButton edge="start" color="inherit" aria-label="back" onClick={handleDocumentBack} style={styles.backButton}>
              <ArrowBackIcon />
            </IconButton>
          </Box>
          
          <Box sx={{ flexGrow: 1, display: 'flex', overflow: 'hidden' }}>
             {isPdf ? (
               <Box style={styles.pdfViewerBox}>
                 {pdfError ? (
                   <Typography sx={{ mt: 5 }}>Failed to load PDF. {pdfError}</Typography>
                 ) : (
                   <Document
                     file={documentUrl}
                     onLoadSuccess={onDocumentLoadSuccess}
                     onLoadError={(error) => setPdfError(error.message)}
                     loading={<Typography sx={{ mt: 5 }}>Loading PDF...</Typography>}
                   >
                     {Array.from(new Array(numPages || 0), (el, index) => (
                       <Box key={`page_${index + 1}`} sx={{ mb: 2, boxShadow: 3 }}>
                         <Page 
                           pageNumber={index + 1} 
                           width={window.innerWidth > 600 ? 600 : window.innerWidth - 20} 
                           renderTextLayer={false} 
                           renderAnnotationLayer={false}
                         />
                       </Box>
                     ))}
                   </Document>
                 )}
               </Box>
            ) : (
               <Box style={styles.imageViewerBox}>
                  <img src={documentUrl} alt="Document Viewer" style={styles.imageViewer} />
               </Box>
            )}
          </Box>
        </Box>
      ) : (
        <Box>
          <Box style={styles.statusContainer}>
            <Heading title="Documents" onBackClick={handleBack} vehicle={vehicleNumber} />
          </Box>
          <Box sx={{ paddingLeft: '16px', paddingRight: '16px' }}>
          <input
              ref={fileInputRef}
              type="file"
              style={styles.inputFile}
              onChange={handleFileChange}
              accept="image/*,application/pdf"
            />
            {Object.keys(getDocumentMapping).map((key, index) => {
              const hasFile = !!uploadedUserFiles[key] || !!uploadedVehicleFiles[key];
              const isUploadingDoc = isUploading[key];
              
              return (
                <Box key={index} style={styles.documentBox}>
                  <Typography variant="body1">{getDocumentMapping[key]}</Typography>
                  <div style={styles.actionContainer}>
                    {!hasFile || isUploadingDoc ? (
                      <IconButton 
                        onClick={() => handleUploadClick(key)}
                        disabled={isUploadingDoc}
                      >
                        <UploadIcon />
                        {isUploadingDoc && ' Uploading...'}
                      </IconButton>
                    ) : (
                      <div style={styles.actionContainer}>
                        <Typography 
                          variant="body1" 
                          style={styles.viewLabel} 
                          onClick={() => handleViewClick(
                            key, 
                            uploadedUserFiles[key]?.location || uploadedVehicleFiles[key]?.location
                          )}
                        >
                          View
                        </Typography>
                        <IconButton onClick={() => handleDeleteClick(key)}>
                          <DeleteIcon />
                        </IconButton>
                      </div>
                    )}
                  </div>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      <ConfirmationPopup
        open={isDeletePopupOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Confirm Deletion"
        message={`Are you sure you want to delete this document?`}
      />
      <FooterLogo />
    </Container>
  );
};

export default DocumentsScreen;